'use client';

import {
  faFacebook,
  faInstagram,
  faLinkedinIn,
  faSnapchat,
  faTiktok,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Session } from '@pickleballinc/lib/session';
import { Badge, Button, createS3ImageStore, ImageEditor } from '@pickleballinc/react-ui';
import { RankingStatusBadge } from '@pickleballinc/ui/components/global/RankingStatusBadge/RankingStatusBadge';
import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { publicEnv } from '@/lib/env';
import { FollowActionButton } from '@/modules/players/v2/header/FollowActionButton';
import { FriendActionButtons } from '@/modules/players/v2/header/FriendActionButtons';
import { ProfileInfoBox } from '@/modules/players/v2/header/ProfileInfoBox';
import { usePlayerHeaderActions } from '@/modules/players/v2/header/usePlayerHeaderActions';
import { ImageWrapper } from '@/modules/players/v2/ImageWrapper';
import { useCreateUserImage } from '@/modules/players/v2/tabs/edit-profile/hooks/hooks';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { PlayerData, UserImage } from '@/types/players.types';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';
import { customRevalidateTag } from '@/utils/helpers/customRevalidateTag';
interface SponsorImage {
  imageUrl: string | null;
  link: string | null;
  name?: string | null;
}

type PlayerHeaderProps = {
  playerData: PlayerData | undefined;
  headerImage: UserImage | undefined;
  profileImage: UserImage | undefined;
  sponsorImage: SponsorImage | undefined;
  isProPlayer: boolean | undefined;
  currentUserIsProPlayer?: boolean;
  isDuprActive: boolean;
  user: Session | undefined;
};

const s3ImageStore = createS3ImageStore({
  bucketPath: 'profile-images',
  uploadUrl: `${publicEnv.NEXT_PUBLIC_IMAGE_UPLOAD_URL}/request`,
});

export const PlayerHeader = ({
  playerData,
  headerImage,
  profileImage,
  sponsorImage,
  isProPlayer,
  currentUserIsProPlayer,
  isDuprActive,
  user,
}: PlayerHeaderProps) => {
  const { hasPermission } = useAuth();
  const { mutate: createUserImage } = useCreateUserImage();
  const [imageUpload, setImageUpload] = useState(false);
  const isHeaderDeclined = headerImage && headerImage.dateOfRejection;
  const isHeaderPendingApproval = headerImage && headerImage.totalApprovals < 2;

  const isProfileImageDeclined = profileImage && profileImage.dateOfRejection;
  const isProfileImagePendingApproval = profileImage && profileImage.totalApprovals < 2;
  const sponsorLink = playerData?.sponsors?.find((sponsor) => sponsor.isPrimary === true)?.url;
  const isSuperAdminOrSupportStaff = hasPermission([
    Permission.SUPER_ADMIN,
    Permission.SUPPORT_STAFF,
  ]);
  const isEditor = hasPermission(Permission.USERS_PROFILES_EDITOR);

  const { isUserVisitingOwnProfile, canSeeMyDashboard, canSeeFriendActions, canSeeFollowActions } =
    usePlayerHeaderActions({ playerData, user, isProPlayer, currentUserIsProPlayer });

  // to avoid iOS scroll bug
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='hidden bg-gradient-to-r from-experiment to-[#0A0026] lg:block'>
      <div
        className='w-full bg-[length:1000px_1000px] py-8'
        style={{
          backgroundImage: `url(${getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/match-stats-bg.svg`, 1000, 1000)})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        {playerData ? (
          <div className='relative mx-auto flex w-full max-w-[996px] rounded-xl border bg-experiment/25 text-white'>
            <div
              className='absolute right-4 top-4 flex flex-col items-end gap-2'
              data-testid={`players-button-edit-profile-outer`}
            >
              {isUserVisitingOwnProfile || isEditor || isSuperAdminOrSupportStaff ? (
                <CustomLink href={`/players/${playerData.slug}/profile/edit`}>
                  <Button
                    variant='primary'
                    size='sm'
                    className='!rounded bg-experiment !px-2 !py-1 !text-sm w-full'
                    data-testid={`players-button-edit-profile`}
                  >
                    <span className='text-center w-full'>Edit Profile</span>
                  </Button>
                </CustomLink>
              ) : null}

              {canSeeMyDashboard && user?.uuid ? (
                <CustomLink href={`/players/${playerData.slug}/v3/profile`}>
                  <Button
                    variant='primary'
                    size='sm'
                    className='!rounded bg-experiment !px-2 !py-1 !text-sm'
                    data-testid={`players-button-edit-profile`}
                  >
                    My Dashboard
                  </Button>
                </CustomLink>
              ) : null}

              <CustomLink
                href={`/head-to-head?type=singles&current_page=1&team_one_player_one=${playerData.uuid}`}
              >
                <Button
                  variant='secondary'
                  size='sm'
                  className='!rounded !px-2 !py-1 !text-sm text-purple-600 hover:!text-purple-700'
                  data-testid={`players-button-head-to-head`}
                >
                  Head to Head
                </Button>
              </CustomLink>

              {canSeeFollowActions && user?.uuid ? (
                <FollowActionButton playerUuid={playerData.uuid} userUuid={user.uuid} />
              ) : null}

              {/* Pros keep friend actions in the action stack — the under-photo spot only exists for amateurs */}
              {canSeeFriendActions && isProPlayer && user?.uuid ? (
                <FriendActionButtons
                  size='sm'
                  playerUuid={playerData.uuid}
                  userUuid={user.uuid}
                  playerFirstName={playerData.firstName}
                  playerLastName={playerData.lastNameAndSuffix}
                  playerSlug={playerData.slug}
                />
              ) : null}
            </div>
            <div className='relative flex w-1/2 items-start justify-center'>
              {isProPlayer && headerImage ? (
                <div className='size-full pt-8'>
                  {isHeaderPendingApproval && !isHeaderDeclined ? (
                    <Badge
                      label='Picture pending approval'
                      size='md'
                      variation='warning'
                      className='!absolute left-2 top-2 text-center'
                    />
                  ) : null}
                  {isHeaderDeclined ? (
                    <Badge
                      label='Picture declined'
                      size='md'
                      variation='error'
                      className='!absolute left-2 top-2 text-center'
                    />
                  ) : null}
                  <div className='relative size-full'>
                    <ImageWrapper
                      src={getCDNURL(headerImage?.url, 497, 438)}
                      className='object-contain'
                      fill
                      fallbackSrc={getCDNURL(
                        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image-pro.svg`,
                        497,
                        438
                      )}
                      alt={`${playerData.firstName} ${playerData?.middleName} ${playerData.lastNameAndSuffix}`}
                    />
                  </div>
                </div>
              ) : (
                <div className='relative flex h-full flex-col items-center justify-center gap-6'>
                  {isProfileImagePendingApproval && !isProfileImageDeclined ? (
                    <Badge
                      label='Picture pending approval'
                      size='md'
                      variation='warning'
                      className='!absolute left-1 top-6 text-center'
                    />
                  ) : null}
                  {isProfileImageDeclined ? (
                    <Badge
                      label='Picture declined'
                      size='md'
                      variation='error'
                      className='!absolute left-8 top-6 text-center'
                    />
                  ) : null}
                  <div className='relative size-48'>
                    <Avatar
                      firstName={playerData.firstName}
                      lastName={playerData.lastNameAndSuffix}
                      src={
                        profileImage?.url
                          ? getCDNURL(profileImage.url, 190, 190, 0, true)
                          : undefined
                      }
                      variant='transparent'
                      fallbackDelayMs={600}
                      className='size-48 border border-white bg-experiment/60'
                      textClassName='text-7xl'
                    >
                      {imageUpload && !profileImage ? (
                        <div className='flex size-full items-center justify-center'>
                          {formatText({ text: 'Loading...', role: TextRole.DescriptiveText })}
                        </div>
                      ) : null}
                    </Avatar>
                    {(hasPermission(Permission.USERS_PROFILES_EDITOR) ||
                      playerData.uuid === user?.uuid) &&
                      !profileImage &&
                      !imageUpload && (
                        <div className='absolute bottom-0 right-0 mb-4 cursor-pointer rounded-full bg-experiment hover:bg-brand-700'>
                          <ImageEditor
                            editorConfig={{
                              aspectRatio: 1,
                            }}
                            store={s3ImageStore}
                            token={user?.token}
                            isCircle
                            width={40}
                            height={40}
                            onImageUpload={(response) => {
                              setImageUpload(true);
                              createUserImage(
                                {
                                  uuid: playerData?.uuid,
                                  data: {
                                    type: 'profileImg',
                                    url: response.pintura.store.uploadURL || '',
                                    firstName: playerData.firstName,
                                    lastName: playerData.lastNameAndSuffix,
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    customRevalidateTag('player_profile');
                                  },
                                  onError: () => toast.error('Something went wrong. Try again.'),
                                }
                              );
                            }}
                            dropzoneClassName='!border-solid !border-white '
                          >
                            <FontAwesomeIcon icon={faPlus} className='!size-[15px]' color='white' />
                          </ImageEditor>
                        </div>
                      )}
                  </div>
                  {canSeeFriendActions && !isProPlayer && user?.uuid ? (
                    <div className='!absolute left-1 bottom-4 w-full'>
                      <FriendActionButtons
                        playerUuid={playerData.uuid}
                        userUuid={user!.uuid}
                        playerFirstName={playerData.firstName}
                        playerLastName={playerData.lastNameAndSuffix}
                        playerSlug={playerData.slug}
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <div className='flex w-1/2 flex-col gap-3 py-6'>
              <div className='flex flex-col'>
                <div className='text-lg font-semibold uppercase'>
                  {playerData.firstName} {playerData?.middleName}
                </div>
                <div className='flex h-fit flex-wrap items-center tracking-tight'>
                  <div className='mr-4 text-5xl font-semibold uppercase leading-[60px]'>
                    {playerData.lastNameAndSuffix.length > 10
                      ? playerData.lastNameAndSuffix.slice(0, 10) + '...'
                      : playerData.lastNameAndSuffix}
                  </div>
                </div>
                <div className='flex flex-row w-full items-center justify-between'>
                  {playerData.nickname !== '' ? (
                    <div className='font-semibold'>{`(${playerData.nickname})`}</div>
                  ) : null}
                  <div className='w-2/3'>
                    {FEATURE_FLAGS.ENABLE_RANKING_BADGES ? (
                      <RankingStatusBadge
                        badgeRank={playerData.badgeRank}
                        rankingStatus={playerData.badgeTitle}
                        showBadge={playerData.showBadgeTitle}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className='flex items-center font-semibold uppercase'>
                {playerData.countryFlag !== '' && playerData.country !== '' && (
                  <>
                    <img
                      src={playerData.countryFlag}
                      width={45}
                      height={24}
                      alt={`${playerData.fullCountry} Flag`}
                      className='mr-2'
                    />
                    <div className='pr-4'>{playerData.country}</div>
                  </>
                )}

                {playerData.proLevelId ? (
                  <div className='border-l pl-4'>{playerData.typeOfPlayer}</div>
                ) : null}
              </div>

              <div className='w-4/5'>
                <ProfileInfoBox playerData={playerData} user={user} isDuprActive={isDuprActive} />
              </div>
              {playerData.proLevelId === 5 && playerData.coach ? (
                <div className='flex items-center gap-x-2'>
                  Coach: <div className='font-bold'>{playerData.coach}</div>
                </div>
              ) : null}
              {isProPlayer ||
              (sponsorImage && sponsorImage.imageUrl) ||
              playerData.facebook ||
              playerData.instagram ||
              playerData.snapchat ||
              playerData.twitter ||
              playerData.linikedIn ||
              playerData.youtube ||
              playerData.tiktok ? (
                <div className='flex items-center justify-between'>
                  {isProPlayer && sponsorImage && sponsorImage?.imageUrl ? (
                    <div className='flex flex-col'>
                      <span className='text-sm'>Sponsor</span>
                      <a
                        href={sponsorLink}
                        target='_blank'
                        aria-label={`Link to player's ${sponsorImage.name} (opens in a new tab)`}
                        rel='noreferrer'
                      >
                        <ImageWrapper
                          src={getCDNURL(sponsorImage.imageUrl, 200, 200)}
                          className='mt-2 h-20 w-32 rounded-lg border bg-white object-contain'
                          fill={false}
                          alt='#sponsor'
                          width={200}
                          height={200}
                        />
                      </a>
                    </div>
                  ) : null}
                  {playerData.facebook ||
                  playerData.instagram ||
                  playerData.snapchat ||
                  playerData.twitter ||
                  playerData.linikedIn ||
                  playerData.youtube ||
                  playerData.tiktok ? (
                    <div className='mb-6 mr-10 flex flex-col'>
                      <span className='text-sm'>Follow {playerData.firstName}</span>
                      <div className='mt-2'>
                        <div className='flex w-full gap-x-6 p-2'>
                          {playerData.facebook ? (
                            <a
                              href={playerData.facebook}
                              target='_blank'
                              aria-label={`Link to player's Facebook profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faFacebook}
                                  size={'xl'}
                                  color='#1877F2'
                                  title='Facebook Icon'
                                  titleId='facebook-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.instagram ? (
                            <a
                              href={playerData.instagram}
                              target='_blank'
                              aria-label={`Link to player's Instagram profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faInstagram}
                                  size={'xl'}
                                  color='#000100'
                                  title='Instagram Icon'
                                  titleId='instagram-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.snapchat ? (
                            <a
                              href={playerData.snapchat}
                              target='_blank'
                              aria-label={`Link to player's Snapchat profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faSnapchat}
                                  size={'xl'}
                                  color='#000100'
                                  title='Snapchat Icon'
                                  titleId='snapchat-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.twitter ? (
                            <a
                              href={playerData.twitter}
                              target='_blank'
                              aria-label={`Link to player's X profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faXTwitter}
                                  size={'xl'}
                                  color='#0F1419'
                                  title='Twitter Icon'
                                  titleId='twitter-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.linikedIn ? (
                            <a
                              href={playerData.linikedIn}
                              target='_blank'
                              aria-label={`Link to player's LinkedIn profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faLinkedinIn}
                                  size={'xl'}
                                  color='#0A66C2'
                                  title='LinkedIn Icon'
                                  titleId='linkedin-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.youtube ? (
                            <a
                              href={playerData.youtube}
                              target='_blank'
                              aria-label={`Link to player's YouTube profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faYoutube}
                                  size={'xl'}
                                  color='#c4302b'
                                  title='Youtube Icon'
                                  titleId='youtube-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                          {playerData.tiktok ? (
                            <a
                              href={playerData.tiktok}
                              target='_blank'
                              aria-label={`Link to player's TikTok profile (opens in a new tab)`}
                              rel='noreferrer'
                            >
                              <div className='flex size-10 items-center justify-center rounded-full bg-white'>
                                <FontAwesomeIcon
                                  icon={faTiktok}
                                  size={'xl'}
                                  color='black'
                                  title='TikTok Icon'
                                  titleId='tiktok-icon'
                                />
                              </div>
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
